import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';

// Define the shape of the notification data
interface Notification {
  id: number;
  data: { url: string };  // Ensure that data has a url field
  message: string;
  isRead: boolean;
  createdAt: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: number) => void;
  fetchNotifications: () => void;
  markAsReadOnServer: (id: number) => Promise<void>;  // New function signature
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Notification provider to wrap around the application
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const authToken = sessionStorage.getItem('token');
  const [loading, setLoading] = useState<boolean>(false);
  const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
    
  // Only call handleSignOutWithError if the user is supposed to be logged in
  // if (!isLoggedIn && authToken) {
  //     handleSignOutWithError();
  //     return;
  // }
  
  const fetchNotifications = useCallback(
    async () => {
    if (isLoggedIn || authToken) {
      try {
        setLoading(true);

        const response = await fetch('https://backend-kyw8.onrender.com/notifications', {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const data = await response.json();

        // Set notifications and unread count
        setNotifications(data.notifications);
        console.log(data.notifications);
        const unread = data.notifications.filter((n: Notification) => !n.isRead).length;
        setUnreadCount(unread);

        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch notifications', error);
        setLoading(false);
      }
    }
  },[authToken, isLoggedIn])

  // Mark a notification as read in the frontend
  const markAsRead = (id: number) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    );
    setUnreadCount((prevCount) => prevCount - 1);
  };

  // Mark a notification as read on the server
  const markAsReadOnServer = async (id: number) => {
    try {
      const response = await fetch(`https://backend-kyw8.onrender.com/notifications/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isRead: true }),
      });

      if (!response.ok) {
        console.log('Failed to mark notification as read');
        throw new Error('Failed to mark notification as read');
      }

      const data = await response.json();
      console.log(data.error);

      // After successful API call, update the local state
      markAsRead(id);
    } catch (error) {
      console.error(`Failed to mark notification ${id} as read`, error);
    }
  };

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, loading, markAsRead, fetchNotifications, markAsReadOnServer }}  // Provide markAsReadOnServer
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use the notification context
export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};
